/**
 * Page Context Detection — Single Source of Truth
 *
 * Determines the current page context from the `data-page-type` attribute on `<body>`.
 * Used by main.tsx to conditionally load modules per context:
 * - Webshop: Header, Footer, Cart, Auth, Analytics
 * - Admin/POS: AdminApp SPA only
 * - Platform: PlatformApp SPA only
 *
 * PERFORMANCE IMPACT:
 * Admin/Platform pages skip webshop chrome mounts in main.tsx (Header, cart, i18n).
 * Combined with modulePreload.polyfill=false, avoids Safari unused-preload warnings.
 */

export interface PageContext {
  /** Raw data-page-type value from <body> */
  pageType: string;
  /** Admin panel or POS (own layout, no webshop chrome) */
  isAdmin: boolean;
  /** Platform admin panel (CF Access protected) */
  isPlatform: boolean;
  /** Webshop pages: need Header, Footer, Cart, Auth components */
  isWebshop: boolean;
}

/**
 * Detect page context from DOM data attributes.
 * Single Source of Truth for context detection — used by all mount functions in main.tsx.
 */
export const getPageContext = (): PageContext => {
  const pageType = document.body.dataset.pageType || "";
  const isAdmin =
    pageType.startsWith("admin") ||
    pageType.startsWith("pos") ||
    pageType === "members-desk" ||
    pageType === "members-balie";
  const isPlatform = pageType === "platform";
  const isMinimalShell = pageType === "upload-photo";
  const isWebshop = !isAdmin && !isPlatform && !isMinimalShell;
  return { pageType, isAdmin, isPlatform, isWebshop };
};
